import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Divider
} from '@mui/material';
import { Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchIncidentReports, IncidentReportClient } from '../services/incidentReport';
import { fetchCrisisLogs, CrisisLogClient } from '../services/crisisLog';
import { getClientPrincipal } from '../services/auth';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`travel-ops-tabpanel-${index}`}
            aria-labelledby={`travel-ops-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const TravelOperationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [incidentReports, setIncidentReports] = useState<IncidentReportClient[]>([]);
    const [crisisLogs, setCrisisLogs] = useState<CrisisLogClient[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedIncidentReport, setSelectedIncidentReport] = useState<IncidentReportClient | null>(null);
    const [selectedCrisisLog, setSelectedCrisisLog] = useState<CrisisLogClient | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);

    // Authorized users for Travel Operations
    const TRAVEL_ADMIN_EMAILS = [
        'si@iodparc.com',
        'diane@iodparc.com',
        'pam@iodparc.com',
        'sadie@iodparc.com',
        'hatty@iodparc.com'
    ];

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const principal = await getClientPrincipal();
            const email = principal?.userDetails?.toLowerCase() || null;
            setUserEmail(email);

            if (!email || !TRAVEL_ADMIN_EMAILS.includes(email)) {
                setAccessDenied(true);
                setIsLoading(false);
                return;
            }

            loadData();
        } catch (err) {
            console.error('Error checking access:', err);
            setAccessDenied(true);
            setIsLoading(false);
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Load incident reports and crisis logs
            const [reports, logs] = await Promise.all([
                fetchIncidentReports(),
                fetchCrisisLogs()
            ]);
            setIncidentReports(reports);
            setCrisisLogs(logs);
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft': return 'default';
            case 'submitted': return 'info';
            case 'under_review': return 'warning';
            case 'closed': return 'success';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'draft': return 'Draft';
            case 'submitted': return 'Submitted';
            case 'under_review': return 'Under Review';
            case 'closed': return 'Closed';
            default: return status;
        }
    };

    const formatDateTime = (datetime: string) => {
        if (!datetime) return '';
        return new Date(datetime).toLocaleString();
    };

    if (accessDenied) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography>
                            You do not have permission to access Travel Operations.
                            This page is restricted to authorized travel operations personnel only.
                        </Typography>
                        {userEmail && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Current user: {userEmail}
                            </Typography>
                        )}
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Travel Operations
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                View and manage incident reports and crisis logs
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="travel operations tabs"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={`Incident Reports (${incidentReports.length})`} />
                    <Tab label={`Crisis Logs (${crisisLogs.length})`} />
                </Tabs>

                {/* Incident Reports Tab */}
                <TabPanel value={tabValue} index={0}>
                    {incidentReports.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No incident reports found.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Date/Time</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Travellers</TableCell>
                                        <TableCell>Incident Types</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell>Completed By</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {incidentReports.map((report) => {
                                        const incidentTypes: string[] = [];
                                        if (report.incidentTypeMedical) incidentTypes.push('Medical');
                                        if (report.incidentTypeSecurity) incidentTypes.push('Security');
                                        if (report.incidentTypeAccident) incidentTypes.push('Accident');
                                        if (report.incidentTypeNearMiss) incidentTypes.push('Near Miss');
                                        if (report.incidentTypeOther) incidentTypes.push('Other');

                                        return (
                                            <TableRow key={report.id} hover>
                                                <TableCell>{report.id}</TableCell>
                                                <TableCell>{formatDateTime(report.incidentDateTime)}</TableCell>
                                                <TableCell>{report.location}</TableCell>
                                                <TableCell>
                                                    {report.travellersInvolved.length > 50
                                                        ? `${report.travellersInvolved.substring(0, 50)}...`
                                                        : report.travellersInvolved}
                                                </TableCell>
                                                <TableCell>
                                                    {incidentTypes.map((type, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={type}
                                                            size="small"
                                                            sx={{ mr: 0.5, mb: 0.5 }}
                                                        />
                                                    ))}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(report.status)}
                                                        color={getStatusColor(report.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{report.ownerName || report.ownerEmail}</TableCell>
                                                <TableCell>{report.completedBy}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setSelectedIncidentReport(report)}
                                                        title="View Details"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Crisis Logs Tab */}
                <TabPanel value={tabValue} index={1}>
                    {crisisLogs.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No crisis logs found.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Crisis Title</TableCell>
                                        <TableCell>Declared</TableCell>
                                        <TableCell>Duty Officer</TableCell>
                                        <TableCell>Team Lead</TableCell>
                                        <TableCell>Log Entries</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {crisisLogs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell>{log.id}</TableCell>
                                            <TableCell>{log.incidentCrisisTitle}</TableCell>
                                            <TableCell>{formatDateTime(log.dateTimeCrisisDeclared)}</TableCell>
                                            <TableCell>{log.dutyOfficerOnCall}</TableCell>
                                            <TableCell>{log.crisisManagementTeamLead}</TableCell>
                                            <TableCell>{log.logEntries.length} entries</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={log.status === 'active' ? 'Active' : 'Closed'}
                                                    color={log.status === 'active' ? 'error' : 'success'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{log.ownerName || log.ownerEmail}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSelectedCrisisLog(log)}
                                                    title="View Details"
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Incident Report Detail Dialog */}
            <Dialog
                open={!!selectedIncidentReport}
                onClose={() => setSelectedIncidentReport(null)}
                maxWidth="md"
                fullWidth
            >
                {selectedIncidentReport && (
                    <>
                        <DialogTitle>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Incident Report #{selectedIncidentReport.id}</Typography>
                                <IconButton onClick={() => setSelectedIncidentReport(null)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Date/Time of Incident</Typography>
                                    <Typography variant="body1">{formatDateTime(selectedIncidentReport.incidentDateTime)}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                                    <Typography variant="body1">{selectedIncidentReport.location}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={getStatusLabel(selectedIncidentReport.status)}
                                        color={getStatusColor(selectedIncidentReport.status)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Travellers Involved</Typography>
                                    <Typography variant="body1">{selectedIncidentReport.travellersInvolved}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Incident Types</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                        {selectedIncidentReport.incidentTypeMedical && <Chip label="Medical" size="small" />}
                                        {selectedIncidentReport.incidentTypeSecurity && <Chip label="Security" size="small" />}
                                        {selectedIncidentReport.incidentTypeAccident && <Chip label="Accident" size="small" />}
                                        {selectedIncidentReport.incidentTypeNearMiss && <Chip label="Near Miss" size="small" />}
                                        {selectedIncidentReport.incidentTypeOther && <Chip label="Other" size="small" />}
                                    </Box>
                                    {selectedIncidentReport.incidentTypeOtherDetails && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Other Details:</strong> {selectedIncidentReport.incidentTypeOtherDetails}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary">Description of Incident</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedIncidentReport.description}</Typography>
                                </Grid>
                                {selectedIncidentReport.immediateActions && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Immediate Actions Taken</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedIncidentReport.immediateActions}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Parties Notified</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                        {selectedIncidentReport.notifiedDutyOfficer && <Chip label="Duty Officer" size="small" color="primary" />}
                                        {selectedIncidentReport.notifiedSecurityFocalPoint && <Chip label="Security Focal Point" size="small" color="primary" />}
                                        {selectedIncidentReport.notifiedMd && <Chip label="MD" size="small" color="primary" />}
                                        {selectedIncidentReport.notifiedClient && <Chip label="Client" size="small" color="primary" />}
                                        {selectedIncidentReport.notifiedAigInsurer && <Chip label="AIG/Insurer" size="small" color="primary" />}
                                    </Box>
                                </Grid>
                                {selectedIncidentReport.followupActions && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Follow-up Actions Required</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedIncidentReport.followupActions}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary">Completed By</Typography>
                                    <Typography variant="body1">{selectedIncidentReport.completedBy}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Completion Date</Typography>
                                    <Typography variant="body1">{new Date(selectedIncidentReport.completedDate).toLocaleDateString()}</Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedIncidentReport(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Crisis Log Detail Dialog */}
            <Dialog
                open={!!selectedCrisisLog}
                onClose={() => setSelectedCrisisLog(null)}
                maxWidth="lg"
                fullWidth
            >
                {selectedCrisisLog && (
                    <>
                        <DialogTitle>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Crisis Log #{selectedCrisisLog.id}</Typography>
                                <IconButton onClick={() => setSelectedCrisisLog(null)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Crisis Title</Typography>
                                    <Typography variant="h6">{selectedCrisisLog.incidentCrisisTitle}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Date/Time Declared</Typography>
                                    <Typography variant="body1">{formatDateTime(selectedCrisisLog.dateTimeCrisisDeclared)}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={selectedCrisisLog.status === 'active' ? 'Active' : 'Closed'}
                                        color={selectedCrisisLog.status === 'active' ? 'error' : 'success'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Duty Officer on Call</Typography>
                                    <Typography variant="body1">{selectedCrisisLog.dutyOfficerOnCall}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Crisis Management Team Lead</Typography>
                                    <Typography variant="body1">{selectedCrisisLog.crisisManagementTeamLead}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Action Log</Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Time</strong></TableCell>
                                                    <TableCell><strong>Action Taken</strong></TableCell>
                                                    <TableCell><strong>By Whom</strong></TableCell>
                                                    <TableCell><strong>Decision/Outcome</strong></TableCell>
                                                    <TableCell><strong>Next Steps</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedCrisisLog.logEntries.map((entry, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{entry.time}</TableCell>
                                                        <TableCell>{entry.actionTaken}</TableCell>
                                                        <TableCell>{entry.byWhom}</TableCell>
                                                        <TableCell>{entry.decisionOutcome || '-'}</TableCell>
                                                        <TableCell>{entry.nextSteps || '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="h6" gutterBottom>Closure Information</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Closure Date/Time</Typography>
                                    <Typography variant="body1">
                                        {selectedCrisisLog.closureDateTime ? formatDateTime(selectedCrisisLog.closureDateTime) : 'Leave empty if crisis is still active'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">After-Action Review Completed?</Typography>
                                    <Typography variant="body1">{selectedCrisisLog.afterActionReviewCompleted ? 'Yes' : 'No'}</Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedCrisisLog(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Container>
    );
};

export default TravelOperationsPage;
